import { Link } from "react-router";
import Container from "@components/Container/Container";
import AboutUsLeadershipDetails from "@routes/Content/layout/AboutUsLayout/components/AboutUsLeadershipDetails";

/**
 * This is the layout for the About Us page.
 */
const AboutUsLayout = (): React.ReactElement => {
  return (
    <Container
      rootClassName="!mt-6 px-3 font-poppins"
      data-testid="about-us-layout-component"
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-10 lg:grid-cols-3">
        {/* About Us */}
        <div className="col-span-1">
          <h2 className="font-poppins font-medium text-gray-850">About Us</h2>
        </div>

        <div className="col-span-2">
          <p>
            {
              "Singapore's only financial daily, The Business Times (BT), is the authoritative guide on local, regional and international business markets. In addition to corporate news and "
            }
            <a
              className="text-verticals-btblue hover:text-black hover:underline"
              href="https://www.businesstimes.com.sg/opinion"
            >
              commentary
            </a>
            {", BT covers the "}
            <a
              className="text-verticals-btblue hover:text-black hover:underline"
              href="https://www.businesstimes.com.sg/garage"
              target="_blank"
              rel="noreferrer"
            >
              startup
            </a>
            {", "}
            <a
              className="text-verticals-btblue hover:text-black hover:underline"
              href="https://www.businesstimes.com.sg/wealth"
              target="_blank"
              rel="noreferrer"
            >
              wealth
            </a>
            {", "}
            <a
              className="text-verticals-btblue hover:text-black hover:underline"
              href="https://www.businesstimes.com.sg/real-estate"
              target="_blank"
              rel="noreferrer"
            >
              property
            </a>
            ,
            <a
              className="text-verticals-btblue hover:text-black hover:underline"
              href="https://www.businesstimes.com.sg/lifestyle"
            >
              lifestyle
            </a>
            {" and "}
            <a
              className="text-verticals-btblue hover:text-black hover:underline"
              href="https://www.businesstimes.com.sg/sme"
              target="_blank"
              rel="noreferrer"
            >
              SME
            </a>
            {" scenes, and tells you what you need to know about "}
            <a
              className="text-verticals-btblue hover:text-black hover:underline"
              href="https://www.businesstimes.com.sg/asean-business"
              target="_blank"
              rel="noreferrer"
            >
              Asean
            </a>
            {" and the "}
            <a
              className="text-verticals-btblue hover:text-black hover:underline"
              href="https://www.businesstimes.com.sg/global-enterprise"
              target="_blank"
              rel="noreferrer"
            >
              world
            </a>
            . It also has a young audience initiative known as
            <a
              className="text-verticals-btblue hover:text-black hover:underline"
              data-saferedirecturl="https://www.google.com/url?q=https://www.businesstimes.com.sg/thrive&amp;source=gmail&amp;ust=1675265004461000&amp;usg=AOvVaw0XtIVVf2CjTgU8yMi_BEXt"
              href="https://www.businesstimes.com.sg/thrive"
              target="_blank"
              rel="noreferrer"
            >
              Thrive
            </a>
            . BT is part of SPH Media.
          </p>
        </div>

        {/* Newsroom Leadership */}
        <div className="col-span-1">
          <h2 className="font-poppins font-medium text-gray-850">
            Newsroom Leadership
          </h2>
        </div>

        <div className="col-span-2">
          <AboutUsLeadershipDetails />
        </div>

        {/* Business Awards */}
        <div className="col-span-1">
          <h2 className="font-poppins font-medium text-gray-850">
            Business Awards
          </h2>
        </div>

        <div className="col-span-2">
          <p>
            BT organises the following annual business awards events in
            conjunction with its partners.
          </p>

          <p>
            <Link
              className="font-bold text-verticals-btblue hover:underline"
              to="/events-awards/singapore-business-awards?ref=about-us"
              reloadDocument
            >
              The Singapore Business Awards
            </Link>
            {
              " was launched in 1985, starting with The Businessman of the Year Award. The following year, the Enterprise Award was introduced to recognise small- and medium-sized enterprises."
            }
          </p>

          <p>
            <Link
              className="font-bold text-verticals-btblue hover:underline"
              to="/events-awards/singapore-corporate-awards?ref=about-us"
              reloadDocument
            >
              The Singapore Corporate Awards
            </Link>
            {
              " recognises excellence in corporate governance, with five awards: Best Managed Board, Best Chief Executive Officer, Best Chief Financial Officer Award, Best Investor Relations Award and Best Annual Report Award."
            }
          </p>

          <p>
            <Link
              className="font-bold text-verticals-btblue hover:underline"
              to="/events-awards/enterprise-50?ref=about-us"
              reloadDocument
            >
              The Enterprise 50 (E50) Awards
            </Link>
            {
              " was launched in 1995 to recognise local, privately-held companies who have contributed to economic development in Singapore and abroad."
            }
          </p>

          <p>
            <Link
              className="font-bold text-verticals-btblue hover:underline"
              to="/events-awards/emerging-enterprise?ref=about-us"
              reloadDocument
            >
              The Emerging Enterprise Award
            </Link>
            {
              " was first launched in 2008 to recognise outstanding small and medium enterprises (SMEs) in Singapore. Winners receives prizes to support their growth and development, such as interest-free loans and professional services and training programmes."
            }
          </p>

          <p>
            <Link
              className="font-bold text-verticals-btblue hover:underline"
              to="/events-awards/sustainability-impact-awards?ref=about-us"
              reloadDocument
            >
              Sustainability Impact Awards
            </Link>
            {
              " was first launched on 1 February 2023, is jointly presented by The Business Times and UOB, with the Centre for Governance and Sustainability (CGS) at the National University of Singapore Business School as the knowledge partner."
            }
          </p>

          <p>
            <Link
              className="font-bold text-verticals-btblue hover:underline"
              to="/events-awards/design-ai-and-tech-awards?ref=about-us"
              reloadDocument
            >
              Design AI and Tech Awards
            </Link>
            {
              " launched in October 2024, is a collaborative initiative presented by The Business Times and SUTD, with SUTD also serving as the knowledge partner for the awards."
            }
          </p>
        </div>

        {/* Community & Philanthropy */}
        <div className="col-span-1">
          <h2 className="font-poppins font-medium text-gray-850">
            Community & Philanthropy
          </h2>
        </div>

        <div className="col-span-2">
          <p>
            <a
              className="text-verticals-btblue hover:text-black hover:underline"
              href="http://baf.sg/"
            >
              <strong>{"The Business Times Budding Artists' Fund"}</strong>
            </a>
          </p>

          <p>
            {
              "In 2005, BT adopted the Budding Artists' Fund, an initiative of The Old Parliament House (2002-2014). The fund was started with a conviction that no child should be denied the opportunity to pursue a training in the arts due to a lack of financial resources. Since 2005, BT BAF has reached out to more than 9,000 beneficiaries through a variety of programmes comprising of art camps, workshops, signature events and fundraising initiatives. Beneficiaries of BT BAF receive a structured arts training at The Little Arts Academy."
            }
          </p>
        </div>

        {/* Advertise with Us */}
        <div className="col-span-1">
          <h2 className="font-poppins font-medium text-gray-850">
            Advertise with Us
          </h2>
        </div>

        <div className="col-span-2">
          <p>
            <Link
              to="/advertise?ref=about-us"
              className="font-bold text-verticals-btblue hover:underline"
              target="_blank"
            >
              Advertising Solutions
            </Link>
            {
              " - Engage your audience through captivating print visuals, engaging online content, or immersive audio experiences."
            }
          </p>

          <p>
            <Link
              to="/advertise?ref=about-us"
              className="font-bold text-verticals-btblue hover:underline"
              target="_blank"
            >
              Content Marketing
            </Link>
            {
              " - Elevate your message with advertorials integrated into editorial content and tailored supplements."
            }
          </p>

          <p>
            <Link
              to="/paid-press-release?ref=about-us"
              className="font-bold text-verticals-btblue hover:underline"
              target="_blank"
            >
              Paid Press Release
            </Link>
            {
              " - Amplify your message with our Paid Press Release service, delivering your message directly to key audiences for maximum strategic exposure and engagement."
            }
          </p>

          <p>
            <Link
              to="/paid-press-release?ref=about-us"
              className="font-bold text-verticals-btblue hover:underline"
              target="_blank"
            >
              Event Sponsorship
            </Link>
            {
              " - Experience our convening power through the prestigious events we organised, featuring awards and custom solutions, uniting business leaders and decision-makers."
            }
          </p>
        </div>
      </div>
    </Container>
  );
};

export default AboutUsLayout;
